import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { FlowTypes, ISelectedAccount } from '@shared/models';
import { Go } from '@store/actions';
import * as fromRootStore from '@store/index';
import { selectMultiITFromSelectedAccount } from '@store/internal-transfers/selectors/multi-internal-transfer.selectors';
import { selectDecedentFirstName } from '@store/selectors/decedent-info/decedent-info.selectors';
import { selectInternalTransferAccountFrom } from '@store/selectors/internal-transfer/internal-transfer.selectors';
import { selectedAccountsFrom } from '@store/selectors/selected-client-accounts/selected-client-accounts.selectors';
import { flowTypeSelection } from '@store/selectors/triage/triage.selectors';
import { CookieService } from 'ngx-cookie-service';
import { combineLatest, Observable, of, switchMap, take } from 'rxjs';

@Injectable()
export class PersonalInfoGuard {
  constructor(
    private globalStore: Store<fromRootStore.State>,
    private cookieService: CookieService,
  ) {}

  canActivate(): Observable<boolean> {
    if (this.cookieService.get('routeGuard') === 'OFF') {
      return of(true);
    }

    return combineLatest([
      this.globalStore.select(flowTypeSelection),
      this.globalStore.select(selectedAccountsFrom),
      this.globalStore.select(selectInternalTransferAccountFrom),
      this.globalStore.select(selectDecedentFirstName),
      this.globalStore.select(fromRootStore.selectInheritedIraDecedentName),
      this.globalStore.select(selectMultiITFromSelectedAccount),
    ]).pipe(
      take(1),
      switchMap(
        ([
          flowType,
          transferFromAccounts,
          internalTransferAccount,
          decedentFirstName,
          inheritedIraDecedentName,
          multiITFromAccount,
        ]) => {
          const hasRequiredState = this.checkRequiredStateByFlow(
            flowType,
            transferFromAccounts,
            internalTransferAccount,
            decedentFirstName,
            inheritedIraDecedentName,
            multiITFromAccount,
          );

          if (!hasRequiredState) {
            const redirectPath = this.getRedirectPathByFlow(flowType);
            this.globalStore.dispatch(Go({ path: [redirectPath] }));
            return of(false);
          }

          return of(true);
        },
      ),
    );
  }

  private checkRequiredStateByFlow(
    flowType: FlowTypes | null,
    transferFromAccounts: ISelectedAccount[] | null,
    internalTransferAccount: ISelectedAccount | null,
    decedentFirstName: string,
    inheritedIraDecedentName: string,
    multiITFromAccount: ISelectedAccount | null,
  ): boolean {
    switch (flowType) {
      case FlowTypes.INTERNAL_TRANSFER:
      case FlowTypes.DIVORCE:
        return !!internalTransferAccount || !!multiITFromAccount;

      case FlowTypes.INHERITANCE:
        // For single inheritance flow, decedentFirstName may be filled later (e.g., trustee flow)
        // Allow access to personal-info even if decedent name is not yet provided
        return true;
      case FlowTypes.MULTI_INHERITANCE:
        return !!decedentFirstName && decedentFirstName.trim().length > 0;
      case FlowTypes.INHERITED_IRA:
        return !!inheritedIraDecedentName && inheritedIraDecedentName.trim().length > 0;
      default:
        return !!transferFromAccounts && transferFromAccounts.length > 0;
    }
  }

  private getRedirectPathByFlow(flowType: FlowTypes | null): string {
    switch (flowType) {
      case FlowTypes.GIFTING:
        return 'gifting/triage';

      case FlowTypes.MULTI_INHERITANCE:
        return 'multi-inheritance/triage';

      case FlowTypes.INTERNAL_TRANSFER:
        return 'internal-transfer/triage';

      case FlowTypes.INHERITED_IRA:
        return 'inherited-ira/triage';

      case FlowTypes.MINOR:
        return 'secure/minor-transfer/transfer-from';

      case FlowTypes.DIVORCE:
        return 'internal-transfer/triage';

      default:
        return 'error';
    }
  }
}
